import { useSnackbar } from 'notistack';
import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
// @mui
import { styled, alpha } from '@mui/material/styles';
import {
  Stack,
  Typography,
  Card,
  CardContent,
  Divider,
  Skeleton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Box,
  Radio,
  CardActionArea,
  Collapse,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
// hooks
import useAuth from '../../hooks/useAuth';
import useResponsive from '../../hooks/useResponsive';
// components
import Iconify from '../../components/Iconify';
import DIOText from '../../components/DIOText';
import CircleNumber from '../../components/CircleNumber';
import SvgIconStyle from '../../components/SvgIconStyle';
import NegotiateCard from './NegotiateCard';
import CandidateProcessStepper from './CandidateProcessStepper';
import {
  MeetingCard,
  MatchingCrewProfile,
  RequestOtherTimeCard,
  NegoExtendDialog,
  SelectPlanWarnDialog,
  RejectOrModifyCrewDialog,
} from "./";
import {
  MeetingDepositPaymentDialog,
} from './deposit';
import {
  BeforeNegotiationCard,
} from './negotiate';
import {
  MessagePopover,
  MessageDialog,
} from './message';
// util
import axios from '../../utils/axios';
import { fKrNegoTimeKST } from '../../utils/formatTime';
import { calculateDiscountPrice } from '../../utils/calculateDiscount';
// config
import { EVENT_ID } from '../../config';
// routes
import EasySalaryDialog from './EasySalaryDialog';
import IsRightSalaryDialog from './IsRightSalaryDialog';
import { InvitationNudgeSnackbar } from '../invitation';
// constants
import { TRIAL_FEE_RATE, LONGTERM_FEE_RATE, RESIENDT_FEE_RATE } from '../../constants';

// ------------------------------------------------------------

const RedText = styled('span')(({ theme }) => ({
  color: theme.palette.error.main,
  fontWeight: theme.typography.fontWeightBold,
}));

const TitleStyle = styled('div')(({ theme }) => ({
  padding: theme.spacing(2, 0),
}));

const FormStyle = styled('div')(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: Number(theme.shape.borderRadius) * 2,
  backgroundColor: theme.palette.background.neutral,
}));

const CaptionSecondaryText = styled("span")(({ theme }) => ({
  ...theme.typography.caption,
  color: theme.palette.text.secondary,
}));

export default function CandidateOfferForm({ jobPost, isLoading, candidate, loadData, next, closeDialog, reloadAll }) {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const router = useRouter();
  const [isSubmit, setIsSubmit] = useState(false);
  const smUp = useResponsive('up', 'sm');
  const mdUp = useResponsive('up', 'md');

  const [bizRejectDialog, setBizRejectDialog] = useState(false);
  const [openExtendDialog, setOpenExtendDialog] = useState(false);
  const requestOtherTime = candidate.status === 'other-3way-time-requested';
  
  const beforeNego = candidate.status === 'waiting-offer-from-crew' || candidate.status === 'crew-negotiate-to-company' || candidate.status === 'company-negotiate-rewrite';
  const running3WayStatus = ["waiting-3way-meeting", "confirm-3way-meeting"];
  const companyNegotiateStatus = ["crew-negotiate-to-company", "company-negotiate-rewrite", "accept"];
  const { remind: fromRemind } = router.query;

  const isStopped = jobPost.status === 'stop' || ["company-stop", "company-3way-no-response", "company-contract-no-response", "crew-contract-no-response"].includes(candidate?.status);
  const canContact = jobPost.status === 'searching' && ["company-3way-no-response"].includes(candidate?.status);
  const isRejected = ["company-reject"].includes(candidate?.status);

  const dnd = window.localStorage.getItem('dioBizEasySalaryDialog');
  const [easySalaryOpen, setEasySalaryOpen] = useState(candidate.status === 'offer-to-company' && dnd !== 'true' && dnd !== true);  // 인건비 비교 팝업은 열어놓기
  const [isRightSalaryOpen, setIsRightSalaryOpen] = useState(false);
  const isOfferRunning = !beforeNego && !requestOtherTime && !companyNegotiateStatus.includes(candidate.status) && !running3WayStatus.includes(candidate.status);
  const after3wayMeeting = candidate.status === 'confirm-3way-meeting' && !!candidate?.pre_meeting_time && new Date(candidate?.pre_meeting_time) < new Date();

  const noAveragePositions = ["finance", "operator", "engineer"];

  // 사전 질문
  const hasQuestion = !!candidate?.modify_task_proposal || (candidate?.questions?.answers?.length > 0);
  // 카드 열고 닫기
  const [runningCardNumber, setRunningCardNumber] = useState(candidate?.status === 'offer-to-company' ? 1 : 100);

  // plan
  const [plan, setPlan] = useState('');
  const [is4weeksExpanded, setIs4weeksExpanded] = useState(false);
  const [isTrialExpanded, setIsTrialExpanded] = useState(false);
  const [isLongtermExpanded, setIsLongtermExpanded] = useState(false);
  const [isOpenPlanWarnDialog, setIsOpenPlanWarnDialog] = useState(false);

  const allowLongTerm = (
    typeof candidate?.longterm_salary === 'number'
    && candidate?.longterm_salary > 0
    && typeof candidate?.longterm_working_hours_per_week === 'number'
    && candidate?.longterm_working_hours_per_week > 0
  );

  const isResidentWork = candidate?.work_type === 'resident';

  // deposit
  const [isOpenDepositPaymentDialog, setIsOpenDepositPaymentDialog] = useState(false);

  // nudge snackbar
  const [isOpenNudge, setIsOpenNudge] = useState(candidate?.status === 'offer-to-company');

  const closeNudge = async () => {
    setIsOpenNudge(false);
  }

  useEffect(() => {
    if (candidate?.status) {
      setRunningCardNumber(candidate?.status === 'offer-to-company' ? 1 : 100);
    }
  }, [candidate]);

  // discount
  const salary = parseInt(candidate?.salary);
  const feeRate = parseInt(candidate?.fee_rate) === 0 ? 0 : isResidentWork ? RESIENDT_FEE_RATE : parseInt(candidate?.fee_rate);
  const {
    fee=0,
    vat=0,
    total=0,
    finSalary=0,
    finFee=0,
    finVat=0,
    finTotal=0,
    dcAmount=0,
    feeDcAmount=0,
  } = calculateDiscountPrice(
    salary,
    feeRate,
    0,
    candidate?.available_discount_amount
  );

  const trialSalary = parseInt(candidate?.trial_salary);
  const trialFeeRate = parseInt(candidate?.fee_rate) === 0 ? 0 : isResidentWork ? RESIENDT_FEE_RATE : allowLongTerm ? TRIAL_FEE_RATE : parseInt(candidate?.fee_rate);
  const {
    fee: trialFee=0,
    vat: trialVat=0,
    total: trialTotal=0,
    finSalary: trialFinSalary=0,
    finFee: trialFinFee=0,
    finVat: trialFinVat=0,
    finTotal: trialFinTotal=0,
    dcAmount: trialDcAmount=0,
    feeDcAmount: trialFeeDcAmount=0,
  } = calculateDiscountPrice(
    trialSalary,
    trialFeeRate,
    0,
    candidate?.available_discount_amount
  );

  const longtermSalary = parseInt(candidate?.longterm_salary);
  const longtermFeeRate = parseInt(candidate?.fee_rate) === 0 ? 0 : isResidentWork ? RESIENDT_FEE_RATE : allowLongTerm ? LONGTERM_FEE_RATE : parseInt(candidate?.fee_rate);
  const {
    fee: longtermFee=0,
    vat: longtermVat=0,
    total: longtermTotal=0,
    finSalary: longtermFinSalary=0,
    finFee: longtermFinFee=0,
    finVat: longtermFinVat=0,
    finTotal: longtermFinTotal=0,
    dcAmount: longtermDcAmount=0,
    feeDcAmount: longtermFeeDcAmount=0,
  } = calculateDiscountPrice(
    longtermSalary,
    longtermFeeRate,
    0,
    candidate?.available_discount_amount
  );

  const savePlan = async () => {
    try {
      setIsSubmit(true);
      const companyId = user?.employee.company[0].id;
      const jobPostId = jobPost?.id;
      const candidateId = candidate?.id;
      const response = await axios.post(
        `/api/company/${companyId}/job-posts/${jobPostId}/candidate/${candidateId}/plan/`,
        {plan}
      );
      setIsSubmit(false);
    } catch (error) {
      setIsSubmit(false);
      enqueueSnackbar('플랜 선택에 실패하였습니다. 관리자에게 문의해주세요', { variant: 'error' });
      console.error(error);
    }
  }

  const select4weeks = () => {
    if (candidate.status !== 'offer-to-company') return ;
    setPlan('4weeks');
    setIs4weeksExpanded(true);
  };

  const selectTrial = () => {
    if (candidate.status !== 'offer-to-company') return ;
    setPlan('trial');
    setIsTrialExpanded(true);
  };

  const selectLongterm = () => {
    if (candidate.status !== 'offer-to-company') return ;
    setPlan('longterm');
    setIsLongtermExpanded(true);
  };

  const toggleExpand4weeks = (e) => {
    e.stopPropagation();
    setIs4weeksExpanded(!is4weeksExpanded);
  };

  const toggleExpandTrial = (e) => {
    e.stopPropagation();
    setIsTrialExpanded(!isTrialExpanded);
  };

  const toggleExpandLongterm = (e) => {
    e.stopPropagation();
    setIsLongtermExpanded((prev) => !prev);
  };

  const handlePlan = (e) => {
    if (candidate.status !== 'offer-to-company') return ;
    setPlan(e.target.value);
    if (e.target.value === '4weeks') setIs4weeksExpanded(true);
    if (e.target.value === 'trial') setIsTrialExpanded(true);
    if (e.target.value === 'longterm') setIsLongtermExpanded(true);
  };

  useEffect(() => {
    if (candidate && candidate?.status !== 'offer-to-company' && candidate?.status !== 'modify-sent') {
      const selectedPlan = candidate?.trial_selected ? 'trial' : candidate?.longterm_selected ? 'longterm' : '4weeks';
      setPlan(selectedPlan);
      setRunningCardNumber(100);
    }
  }, [candidate]);

  const closeBizRejectDialog = async () => {
    setBizRejectDialog(false);
  };

  const openBizRejectDialog = async () => {
    setBizRejectDialog(true);
  };

  const passOffer = async () => {
    reloadAll();
  };

  const modifyOffer = async () => {
    reloadAll();
  };

  const reserve3WayMeeting = () => {
    setIsOpenDepositPaymentDialog(false);
    savePlan();
    next(true);
  };

  const reserve3WayMeetingOrSaveCard = () => {
    // plan check
    if (!plan) {
      setIsOpenPlanWarnDialog(true);
      return;
    };

    // card check
    if (user?.employee.company[0].need_card) {
      setIsOpenDepositPaymentDialog(true);
      return ;
    }

    reserve3WayMeeting();
  };

  const extendNego = async (reason) => {
    try {
      setIsSubmit(true);
      const companyId = user?.employee.company[0].id;
      const jobPostId = jobPost?.id;
      const candidateId = candidate?.id;
      const response = await axios.post(
        `/api/company/${companyId}/job-posts/${jobPostId}/candidate/${candidateId}/negotiate-extend/`,
        {reason: reason}
      );
      setIsSubmit(false);
      setOpenExtendDialog(false);
      enqueueSnackbar('기한이 연장되었습니다.');
      loadData();
    } catch (error) {
      setIsSubmit(false);
      enqueueSnackbar('기한 연장에 실패하였습니다. 관리자에게 문의해주세요', { variant: 'error' });
      console.error(error);
    }
  }

  const stopJobPost = async (data) => {
    setIsSubmit(true);
    const companyId = user?.employee.company[0].id;
    const jobPostId = jobPost?.id;
    try {
      const stopData = {
        reason: data.reason,
      };
      const response = await axios.post(
        `/api/company/${companyId}/job-posts/${jobPostId}/stop/`,
        stopData
      );
      setIsSubmit(false);
      enqueueSnackbar('공고를 중지하였습니다.');
      closeDialog();
    } catch (error) {
      setIsSubmit(false);
      enqueueSnackbar('공고 중지에 실패하였습니다. 관리자에게 문의해주세요', { variant: 'error' });
      console.error(error);
    }
  }

  if (isLoading) {
    return (
      <Stack spacing={3}>
        <Card variant="outlined">
          <Skeleton variant="rectangular" height={88} />
        </Card>
        <Card variant="outlined">
          <Skeleton variant="rectangular" height={300} />
        </Card>
        <Card variant="outlined">
          <Skeleton variant="rectangular" height={300} />
        </Card>
      </Stack>
    );
  };

  return (
    <>
      {smUp && <CandidateProcessStepper status={candidate.status} />}
      {isLoading
        ?
        <Skeleton variant="text" />
        :
        isStopped
          ?
          <TitleStyle>
            <Typography variant="h4">
              중단된 매칭입니다.
            </Typography>
          </TitleStyle>
          : isRejected ? (
            <TitleStyle>
              <Typography variant="h4">
                회사에서 거절한 매칭입니다.
              </Typography>
            </TitleStyle>
          )
          : <TitleStyle>
            {candidate && running3WayStatus.includes(candidate?.status) && !after3wayMeeting && (
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack>
                <Typography variant="h4" gutterBottom>
                  {fromRemind
                    ? "곧 크루와의 미팅이 시작됩니다. 제시간에 입장해주세요!"
                    : "크루와의 미팅 일정이 확정되었습니다."
                  }
                </Typography>
                {!fromRemind && (
                <Typography variant={smUp ? "body1" : "body2"} paragraph>
                  크루와의 사전 미팅 시간을 확인하고, 미팅을 준비해주세요!
                </Typography>
                )}
                {!smUp && (
                  <Box sx={{ mb: 2 }}>
                    <MessageDialog jobPostId={jobPost?.id} candidate={candidate} />
                  </Box>
                )}
              </Stack>
              {smUp && (
                <Box>
                  <MessagePopover jobPostId={jobPost?.id} candidate={candidate} />
                </Box>
              )}
            </Stack>
            )}
            {candidate && requestOtherTime && (
              <>
                <Typography variant="h4" gutterBottom>
                  크루에게 다른 미팅 시간을 요청했습니다.
                </Typography>
                <Typography variant={smUp ? "body1" : "body2"} gutterBottom>
                  크루가 다른 미팅 가능 시간을 선택하면 카카오톡으로 안내해드릴게요.
                </Typography>
              </>
            )}
            {candidate && !requestOtherTime && !running3WayStatus.includes(candidate?.status) && !beforeNego && !candidate?.status?.includes('accept', 'crew-negotiate-to-company', 'company-negotiate-rewrite') && (
              <>
                {(candidate.status !== 'offer-to-company') && (candidate.status !== 'modify-sent') && (
                  <Typography variant="h4">
                    매칭된 크루의 제안
                  </Typography>
                )}
                {(candidate.status !== 'offer-to-company') && (
                  <Typography variant="h4">
                    크루에게 계약 조건 조정을 요청했어요.
                  </Typography>
                )}
                {(candidate.status === 'offer-to-company') && (
                  <Box>
                    <Typography variant="h4" gutterBottom>
                      {candidate?.coffee_chat ? "커피챗 한 " : "매칭된 "}크루를 확인하고,{!smUp && <br />} Zoom 미팅 시간을 정해주세요.
                    </Typography>
                    <Typography gutterBottom>
                      아래 내용을 모두 확인하면, 미팅을 예약{candidate?.coffee_chat ? "할 수 있어요." : "하거나 다른 크루를 매칭받을 수 있어요."}
                    </Typography>
                  </Box>
                )}
              </>
            )}
            {candidate && (candidate.status === 'waiting-offer-from-crew' || after3wayMeeting) && (
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Stack>
                  {candidate?.coffee_chat ? (
                    <Typography variant="h4" gutterBottom>
                      커피챗 했던 크루의 제안을 기다려주세요.
                  </Typography>  
                  ) : (
                    <Typography variant="h4" gutterBottom>
                        미팅은 잘 마치셨나요? <br/>
                        크루의 제안을 기다려주세요.
                    </Typography>
                  )}
                  <Typography paragraph>
                    계약 조건은 크루가 먼저 제안합니다.
                  </Typography>
                  {!smUp && (
                    <Box sx={{ mb: 2 }}>
                      <MessageDialog jobPostId={jobPost?.id} candidate={candidate} />
                    </Box>
                  )}
                </Stack>
                {smUp && (
                  <Box>
                    <MessagePopover jobPostId={jobPost?.id} candidate={candidate} />
                  </Box>
                )}
              </Stack>
            )}
            {candidate && candidate.status == 'crew-negotiate-to-company' && (
            <Stack>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Typography variant="h4" gutterBottom>
                  {!!candidate.nego_deadline && (<RedText>{fKrNegoTimeKST(candidate.nego_deadline)}까지 {!smUp && <br/>}</RedText>)} 계약을 결정해주세요.
                </Typography>
                <Box>
                  {smUp ? (
                    <MessagePopover jobPostId={jobPost?.id} candidate={candidate} />
                  ) : (
                    <MessageDialog jobPostId={jobPost?.id} candidate={candidate} />
                  )}
                </Box>
              </Stack>
              {!!candidate?.nego_deadline && (
                <Typography variant="body2">
                  기한 내에 결정하지 않으면 이번 크루와 매칭이 중단됩니다.
                </Typography>
              )}
            </Stack>
            )}
            {candidate && candidate.status == 'company-negotiate-rewrite' && (
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack>
                <Typography variant="h4">
                  크루에게 계약조건 수정을 요청하였습니다
                </Typography>
                {!smUp && (
                  <Box sx={{ my: 2 }}>
                    <MessageDialog jobPostId={jobPost?.id} candidate={candidate} />
                  </Box>
                )}
              </Stack>
              {smUp && (
                <Box>
                  <MessagePopover jobPostId={jobPost?.id} candidate={candidate} />
                </Box>
              )}
            </Stack>
            )}
            {candidate && candidate.status == 'accept' && (
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
              <Stack spacing={1}>
                <Typography variant="h4">
                  계약이 확정되었습니다.
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: "keep-all" }}>
                  계약서와 입금 계좌를 카카오톡으로 보내드렸습니다. 계약을 진행해주세요.
                </Typography>
              </Stack>
              <Box sx={{ minWidth: 110, display: "flex", justifyContent: "end" }}>
                {smUp ? (
                  <MessagePopover jobPostId={jobPost?.id} candidate={candidate} />
                ) : (
                  <MessageDialog jobPostId={jobPost?.id} candidate={candidate} />
                )}
              </Box>
            </Stack>
            )}
          </TitleStyle>
      }

      {/* 기한 중단된 크루 재연락 안내 */}
      {canContact ? (
        <Box
          sx={{
            p: 2, backgroundColor: (theme) => alpha(theme.palette.info.main, 0.08), borderRadius: 2, border: '1px solid', borderColor: "info.main",
            "&:hover": {
              backgroundColor: (theme) => alpha(theme.palette.info.main, 0.16),
              cursor: "pointer",
            },
            "&:active": {
              backgroundColor: (theme) => alpha(theme.palette.info.main, 0.24),
            },
          }}
          onClick={() => {window.open("http://pf.kakao.com/_xbEWUs/chat", "_blank")}}
        >
          <Stack direction="row" spacing={1.5}>
            <Iconify icon={"eva:alert-circle-fill"} sx={{ width: 24, height: 24, color: 'info.main' }} />
            <Stack direction={{ xs: "column", md: "row" }} alignItems={mdUp ? "center" : "flex-start"} justifyContent="space-between" sx={{ width: 1 }} spacing={1}>
              <Stack spacing={.5}>
                <Typography variant={mdUp ? "subtitle1" : "subtitle2"} color="info.darker">
                  미팅을 진행하고 싶으신가요?
                </Typography>
                <Typography variant="body2" color="info.darker">
                  답변 기한을 놓쳤지만 크루와 미팅을 진행하고 싶으시다면, <DIOText /> 카카오톡으로 문의해주세요.
                </Typography>
              </Stack>
              <Button
                sx={{ color: "info.darker", borderColor: "info.darker" }} variant="outlined" size="small"
              >
                <DIOText />에 문의하기
              </Button>
            </Stack>
          </Stack>
        </Box>
      ) : (<></>)}

      {/* 계약조건 조정안내 */}
      {candidate && candidate?.status === 'modify-sent' && (
        <Box sx={{ p: 2.5, mb: 1, backgroundColor: (theme) => alpha(theme.palette.info.main, 0.08), borderRadius: 2, border: '1px solid', borderColor: "info.main" }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={smUp ? 1.5 : 1}>
            <Iconify icon={"eva:info-fill"} sx={{ minWidth: smUp ? 24 : 20, minHeight: smUp ? 24 : 20, width: smUp ? 24 : 20, height: smUp ? 24 : 20, color: 'info.main' }} />
            <Stack spacing={2}>
              <Stack>
                <Typography variant={"subtitle1"} color="info.darker">
                  크루에게 요청한 계약 조정 내용
                </Typography>
                <Typography variant={"caption"} color="info.darker">
                  크루가 답변을 보내면 알려드릴게요.
                </Typography>
              </Stack>

              <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <SvgIconStyle src="/icons/icon-pencil-mono.svg" sx={{ width: 16, height: 16, color: "info.dark" }} />
                  <Typography variant="subtitle2">
                    조정 요청한 항목
                  </Typography>
                </Stack>
                <Typography variant="body2">
                  {candidate?.modify_items}
                </Typography>
              </Stack>

              <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Iconify icon="eva:email-fill" sx={{ width: 16, height: 16, color: "info.dark" }} />
                  <Typography variant="subtitle2">
                    크루에게 보낸 메시지
                  </Typography>
                </Stack>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {candidate?.modify_message}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        </Box>
      )}

      {candidate && (beforeNego || after3wayMeeting) && (
        <BeforeNegotiationCard
          jobPost={jobPost}
          candidate={candidate}
          loadData={loadData}
          openExtendDialog={() => setOpenExtendDialog(true)}
        />
      )}

      {candidate && companyNegotiateStatus.includes(candidate.status) && (
        <NegotiateCard candidate={candidate} jobPost={jobPost} loadData={loadData} />
      )}

      {/* 사전미팅 */}
      {candidate && running3WayStatus.includes(candidate.status) && !after3wayMeeting && (
        <MeetingCard jobPost={jobPost} candidate={candidate} />
      )}

      {/* 사전미팅 시간 변경요청 */}
      {candidate && requestOtherTime && (
        <RequestOtherTimeCard candidate={candidate} />
      )}

      {/* 크루 정보 */}
      {candidate && (
      <Accordion
        sx={{
          p: 0,
          '&:before': {
            display: 'none',
          },
          '&.Mui-expanded': {
            my: 0,
          }
        }}
        defaultExpanded={isOfferRunning || running3WayStatus.includes(candidate.status)}>
        {!isOfferRunning && (
        <AccordionSummary
          sx={{
            my: 1,
            '&.Mui-expanded': {
              my: 0,
            }
          }}
          expandIcon={<Iconify icon={'eva:arrow-ios-downward-fill'} width={20} height={20} />}
        >
          <Typography variant="h6" sx={{ color: 'text.secondary' }}>크루의 상세 정보{candidate?.coffee_chat ? "" : " 및 계약 플랜"}</Typography>
        </AccordionSummary>
        )}
        <AccordionDetails sx={{ p: 0 }}>
          {/* 추천 */}
          {candidate?.summary && (
            <Stack
              sx={{
                backgroundColor: "primary.main",
                borderRadius: 2,
                p: 2.5,
                mb: 3,
              }}
              spacing={1.5}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <SvgIconStyle src={`/icons/icon-medal-check-mono.svg`} sx={{ color: "warning.main", minWidth: 20, minHeight: 20, maxWidth: 20, maxHeight: 20 }} />
                <Typography variant="h6" sx={{ color: "common.white" }}>
                  강력 추천하는 {candidate?.summary?.split('\n')?.filter(m => m)?.length}가지 이유
                </Typography>
              </Stack>
              <Stack spacing={1} sx={{ width: 1, overflow: "hidden", color: "common.white", pl: 1 }}>
              {candidate?.summary?.split('\n').filter(m => m).map((line, index) => (
                <Stack key={index} direction="row" spacing={1} alignItems="flex-start">
                  <Typography variant="body2">
                    •
                  </Typography>
                  <Typography variant="body2">
                    {line}
                  </Typography>
                </Stack>
              ))}
              </Stack>
            </Stack>
          )}
          {/* 1-before */}
          {runningCardNumber == 1 && (
          <Card
            variant="outlined"
            sx={{
              mb: 3,
              borderColor: "primary.main",
              '&:hover': {
                cursor: "pointer",
                backgroundColor: "grey.200",
              },
              '&:active': {
                opacity: 1,
                backgroundColor: "grey.300",
              },
            }}
            onClick={() => setRunningCardNumber(2)}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack alignItems="center" direction="row" spacing={1}>
                <CircleNumber number={1} />
                <Typography variant="h5">
                  크루 프로필 확인하기
                </Typography>
                <Iconify icon={'eva:arrow-ios-downward-fill'} width={24} height={24} />
              </Stack>
            </CardContent>
          </Card>
          )}

          {/* 1-after */}
          <Collapse
            in={runningCardNumber != 1}
            {...(runningCardNumber === 1 ? { timeout: 500 } : {})}
          >
          <Card
            sx={{
              mb: 3,
              '&:hover': {
                boxShadow: (theme) => theme.customShadows.z24,
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack alignItems="center" direction="row" spacing={1} sx={{ mb: 4 }}>
                <CircleNumber number={1} />
                <Typography variant="h5">
                  크루 프로필 {candidate.status === 'offer-to-company' && '확인하기'}
                </Typography>
              </Stack>
              <MatchingCrewProfile jobPost={jobPost} candidate={candidate} />
            </CardContent>
          </Card>
          </Collapse>

          {/* 2-before */}
          {hasQuestion && runningCardNumber <= 2 && (
          <Card
            variant="outlined"
            sx={{
              mb: 3,
              borderColor: runningCardNumber < 2 ? "grey.200" : "primary.main",
              backgroundColor: runningCardNumber < 2 ? "grey.200" : "transparent",
              ...(runningCardNumber ===2) && {
                '&:hover': {
                  cursor: "pointer",
                  backgroundColor: "grey.200",
                },
                '&:active': {
                  opacity: 1,
                  backgroundColor: "grey.300",
                },
              }
            }}
            onClick={() => setRunningCardNumber(runningCardNumber === 2 ? 3 : runningCardNumber)}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack alignItems="center" direction="row" spacing={1}>
                <CircleNumber
                  number={2}
                  sx={{
                    backgroundColor: "grey.400",
                    ...(runningCardNumber === 2) && {
                      backgroundColor: "primary.main",
                    }
                  }}
                />
                <Typography variant="h5"
                  sx={{
                    color: "text.disabled",
                    ...(runningCardNumber === 2) && {
                      color: "text.primary",
                    }
                  }}
                >
                  <DIOText /> 인터뷰 확인하기
                </Typography>
                <Iconify icon={'eva:star-fill'} sx={{ width: 24, height: 24, color: 'info.main' }} />
                {(runningCardNumber === 2) && (
                <Iconify icon={'eva:arrow-ios-downward-fill'} width={24} height={24} />
                )}
              </Stack>
            </CardContent>
          </Card>
          )}

          {/* 2-after */}
          <Collapse
            in={hasQuestion && runningCardNumber > 2}
            {...(runningCardNumber === 2 ? { timeout: 500 } : {})}
          >
            <Card
              sx={{
                mb: 3,
                '&:hover': {
                  boxShadow: (theme) => theme.customShadows.z24,
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Stack alignItems="center" direction="row" spacing={1} sx={{ mb: 2 }}>
                  <CircleNumber number={2} />
                  <Typography variant="h5">
                    <DIOText /> 인터뷰 {candidate.status === 'offer-to-company' && '확인하기'}
                  </Typography>
                  <Iconify icon={'eva:star-fill'} sx={{ width: 24, height: 24, color: 'info.main' }} />
                </Stack>
                <Typography sx={{ pb: 1, color: "text.secondary" }}>
                  올려두신 공고에 적합한 크루인지 확인하기 위한 인터뷰를 진행했어요.
                  {candidate.status === 'offer-to-company' && ' 더 궁금한 점이 있다면, Zoom 미팅에서 크루에게 질문해보세요.'}
                </Typography>

                {candidate?.questions?.answers?.map((answer, index) => (
                  <Stack key={index} sx={{ mt: 3 }}>
                    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1 }}>
                      <SvgIconStyle src={`/icons/icon-mic-mono.svg`} sx={{ color: "text.disabled" }} />
                      <Typography variant="h6" sx={{ whiteSpace: "pre-wrap" }}>
                        {answer.question}
                      </Typography>
                    </Stack>
                    {answer.question_type === "yes-no" ? (
                      <Stack sx={{ pl: 4 }} spacing={.5}>
                        {answer.yn_answer !== null && (
                        <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                          {answer.yn_answer}
                        </Typography>
                        )}
                        {answer.yn_answer !== null && (
                        <Typography variant="subtitle1" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                          {answer.yn_answer === "네" ? answer.yes_followup : answer.no_followup}
                        </Typography>
                        )}
                        <Typography sx={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                          {answer.answer}
                        </Typography>
                      </Stack>
                    ) : (
                    <Typography sx={{ pl: 4, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                      {answer.answer}
                    </Typography>
                    )}
                    {(index < candidate?.questions?.answers?.length - 1) && (<Divider sx={{ mt: 3 }} />)}
                  </Stack>
                ))}
                {candidate?.modify_task_proposal && (
                  <Stack sx={{ mt: 3 }}>
                    {hasQuestion && (<Divider sx={{ mb: 3 }} />)}
                    <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1 }}>
                      <SvgIconStyle src={"/icons/icon-chat-bubble-question-mono.svg"} sx={{ color: "text.disabled" }} />
                      <Typography variant="h6">
                        회사에 궁금한 점이나 업무에 대해 제안하실 것이 있다면 알려주세요.
                      </Typography>
                    </Stack>
                    <Typography sx={{ pl: smUp ? 4 : 0, whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                      {candidate?.modify_task_proposal}
                    </Typography>
                  </Stack>
                )}
              </CardContent>
            </Card>
          </Collapse>

          {/* 3-before */}
          {runningCardNumber <= (hasQuestion ? 3 : 2) && (
          <Card
            variant="outlined"
            sx={{
              mb: 3,
              borderColor: runningCardNumber < (hasQuestion ? 3 : 2) ? "grey.200" : "primary.main",
              backgroundColor: runningCardNumber < (hasQuestion ? 3 : 2) ? "grey.200" : "inherit",
              ...(runningCardNumber === (hasQuestion ? 3 : 2)) && {
                '&:hover': {
                  cursor: "pointer",
                  backgroundColor: "grey.200",
                },
                '&:active': {
                  opacity: 1,
                  backgroundColor: "grey.300",
                },
              }
            }}
            onClick={() => setRunningCardNumber(runningCardNumber === (hasQuestion ? 3 : 2) ? 100 : runningCardNumber)}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack alignItems="center" direction="row" spacing={1}>
                <CircleNumber
                  number={hasQuestion ? 3 : 2}
                  sx={{
                    backgroundColor: "grey.400",
                    ...(runningCardNumber === (hasQuestion ? 3 : 2)) && {
                      backgroundColor: "primary.main",
                    }
                  }}
                />
                <Typography variant="h5"
                  sx={{
                    color: "text.disabled",
                    ...(runningCardNumber === (hasQuestion ? 3 : 2)) && {
                      color: "text.primary",
                    }
                  }}
                >
                  계약 플랜 선택하기
                </Typography>
                {(runningCardNumber === (hasQuestion ? 3 : 2)) && (
                <Iconify icon={'eva:arrow-ios-downward-fill'} width={24} height={24} />
                )}
              </Stack>
            </CardContent>
          </Card>
          )}

          {/* 3-after */}
          <Collapse
            in={runningCardNumber > (hasQuestion ? 3 : 2) && (!jobPost?.coffee_chat || (candidate?.working_hours_per_week && candidate?.salary))}
            {...(runningCardNumber === (hasQuestion ? 3 : 2) ? { timeout: 500 } : {})}
          >
          <Card
            variant="outlined"
            sx={{
              mb: 3,
              '&:hover': {
                boxShadow: (theme) => theme.customShadows.z24,
              },
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack sx={{ mb: 3 }} spacing={1}>
                {candidate.status === 'offer-to-company' && (
                  <Stack sx={{ mb: 1 }} spacing={.5}>
                    <Stack alignItems="center" direction="row" spacing={1} sx={{ mb: 1 }}>
                      <CircleNumber number={hasQuestion ? 3 : 2} />
                      <Typography variant="h5">
                        계약 플랜 선택하기
                      </Typography>
                    </Stack>
                    <Typography>
                      미팅 예약 전, 원하는 계약 플랜을 선택해주세요. 계약 플랜과 크루의 제안 금액, 주당 업무 시간은 크루와 미팅때 협의 후 변경할 수 있어요.
                    </Typography>
                  </Stack>
                )}
                {candidate.status === 'offer-to-crew' && (
                  <Stack sx={{ mb: 1 }} spacing={.5}>
                    <Stack alignItems="center" direction="row" spacing={1} sx={{ mb: 1 }}>
                      <CircleNumber number={hasQuestion ? 3 : 2} />
                      <Typography variant="h5">
                        계약 플랜
                      </Typography>
                    </Stack>
                    <Typography>
                      크루가 제안한 계약 조건이에요. 크루의 제안 금액, 주당 업무 시간은 크루와 미팅때 협의 후 변경할 수 있어요.
                    </Typography>
                  </Stack>
                )}
                {candidate.status !== 'offer-to-company' && candidate.status !== 'offer-to-crew' && (
                  <Stack sx={{ mb: 1 }} spacing={.5}>
                    <Stack alignItems="center" direction="row" spacing={1} sx={{ mb: 1 }}>
                      <CircleNumber number={hasQuestion ? 3 : 2} />
                      <Typography variant="h5">
                        선택한 계약 플랜
                      </Typography>
                    </Stack>
                    <Typography>
                      Zoom 미팅 이전에 선택했던 계약 플랜이에요.
                    </Typography>
                  </Stack>
                )}

                {!noAveragePositions.includes(jobPost?.position) && (
                <Stack>
                  <Button
                    disableRipple
                    size="small"
                    variant="outlined"
                    sx={{
                      py: 0.5,
                      mb: 2,
                      borderRadius: 1,
                      borderColor: 'grey.300',
                      maxWidth: 'max-content',
                      justifyContent: 'start',
                      color: 'text.secondary',
                      fontWeight: (theme) => theme.typography.fontWeightBold,
                    }}
                    fullWidth={false}
                    endIcon={<Iconify icon={'eva:arrow-ios-forward-fill'} />}
                    onClick={() => setIsRightSalaryOpen(true)}
                  >
                    이 금액이 적당한지 궁금하신가요?
                  </Button>
                </Stack>
                )}

                {allowLongTerm ? (
                <Stack sx={{ width: 1 }} direction={{ xs: "column", sm: "column", db: "row" }} alignItems="stretch" spacing={2}>
                  {/* 4 + 2주 플랜 + 8주 플랜 */}
                  {/* 4주 플랜 */}
                  <Card
                    sx={{
                      width: 1,
                      borderRadius: 2,
                      border: "2px solid rgba(145, 158, 171, 0.24)",
                      ...(plan === "4weeks") && {
                        backgroundColor: "grey.200",
                        border: (theme) => `2px solid ${theme.palette.secondary.main}`,
                      },
                      ...candidate.status !== 'offer-to-company' && {
                        boxShadow: "none",
                      }
                    }}
                  >
                    <CardActionArea sx={{ p: 3 }} onClick={() => select4weeks()} disabled={candidate.status !== 'offer-to-company'}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="subtitle1">
                            4주 정식 계약
                          </Typography>
                          <Typography variant="subtitle2" sx={{ color: "text.disabled" }}>
                            플랜
                          </Typography>
                        </Stack>
                        <Radio color="secondary" value="4weeks" checked={plan==="4weeks"} onChange={(e) => handlePlan(e)} />
                      </Stack>

                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="h4" color={"secondary.main"}>{(salary/10000).toLocaleString()}만원</Typography>
                        <Typography variant="h6" color={"secondary.main"}>/ 4주</Typography>
                      </Stack>

                      <Stack direction="row" spacing={.5} alignItems="center" sx={{ my: 1 }}>
                        <SvgIconStyle src={`/icons/ic_clock.svg`} sx={{ width:20, height: 20, color: "text.disabled" }} />
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          <b>주 {candidate.working_hours_per_week}시간</b> 근무
                        </Typography>
                      </Stack>

                      <Collapse
                        in={smUp || is4weeksExpanded}
                        {...(is4weeksExpanded ? { timeout: 500 } : {})}
                      >
                      <Typography variant="body2">
                        기업에서 가장 많이 선택하는 플랜이에요. <br/>
                        즉시 업무 착수가 필요한 경우 4주 플랜으로 정식 계약을 진행해보세요.
                      </Typography>

                      <Divider sx={{ my: 3 }} />

                      <Stack spacing={.5}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <SvgIconStyle src={`/icons/icon-coin-mono.svg`} sx={{ color: "text.disabled" }} />
                          <Typography variant="h6">
                            계약 금액
                          </Typography>
                        </Stack>
                        <Stack sx={{ pl: 3 }} spacing={1}>
                          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ backgroundColor: "rgba(51, 102, 255, 0.08)", px: 2, py: 1, borderRadius: 1 }}>
                            <Typography variant="subtitle2">
                              총 금액
                            </Typography>
                            <Typography variant="subtitle1">
                              {finTotal.toLocaleString()}원
                            </Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between" sx={{ px: 2 }}>
                            <Typography variant="body2">
                              크루 인건비
                            </Typography>
                            <Typography variant="subtitle2" sx={{ color: "secondary.main" }}>
                              {salary.toLocaleString()}원
                            </Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between" sx={{ px: 2 }}>
                            <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                              수수료 ({feeRate}%)
                            </Typography>
                            <Typography variant="subtitle2">
                              {fee.toLocaleString()}원
                            </Typography>
                          </Stack>
                          {feeDcAmount > 0 && (
                          <Stack direction="row" justifyContent="space-between" sx={{ px: 2 }}>
                            <Typography variant="body2">
                              첫달 수수료할인 {!smUp && <br />}<CaptionSecondaryText>예상금액</CaptionSecondaryText>
                            </Typography>
                            <Typography variant="body2">
                              - {feeDcAmount.toLocaleString()}원
                            </Typography>
                          </Stack>
                          )}
                          {dcAmount > 0 && (
                          <Stack direction="row" justifyContent="space-between" sx={{ px: 2 }}>
                            <Typography variant="body2">
                              쿠폰할인 {!smUp && <br />}<CaptionSecondaryText>예상금액</CaptionSecondaryText>
                            </Typography>
                            <Typography variant="body2">
                              - {dcAmount.toLocaleString()}원
                            </Typography>
                          </Stack>
                          )}
                          <Stack direction="row" justifyContent="space-between" sx={{ px: 2 }}>
                            <Typography variant="body2">
                              부가세
                            </Typography>
                            <Typography variant="body2">
                              {finVat.toLocaleString()}원
                            </Typography>
                          </Stack>
                        </Stack>
                      </Stack>
                      </Collapse>
                    </CardActionArea>

                    {/* collapse button */}
                    {!smUp && (
                      <Button fullWidth onClick={toggleExpand4weeks} size="small"
                        sx={{ mb: 2, color: "text.disabled" }}
                        endIcon={<Iconify icon={is4weeksExpanded ? "eva:chevron-up-fill" : "eva:chevron-down-fill"} sx={{ width: 24, height: 24, color: "text.disabled" }} />}
                      >
                        {is4weeksExpanded ? "간략히 보기" : "금액 상세 정보 보기"}
                      </Button>
                    )}
                  </Card>

                  {/* 2주 trial */}
                  {!isResidentWork && (
                  <Card
                    sx={{
                      width: 1,
                      borderRadius: 2,
                      border: "2px solid rgba(145, 158, 171, 0.24)",
                      ...(plan === "trial") && {
                        backgroundColor: "grey.200",
                        border: (theme) => `2px solid ${theme.palette.secondary.main}`,
                      },
                      ...candidate.status !== 'offer-to-company' && {
                        boxShadow: "none",
                      }
                    }}
                  >
                    <CardActionArea sx={{ p: 3 }} onClick={() => selectTrial()} disabled={candidate.status !== 'offer-to-company'}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="subtitle1">
                            2주 단기 체험판
                          </Typography>
                          <Typography variant="subtitle2" sx={{ color: "text.disabled" }}>
                            플랜
                          </Typography>
                        </Stack>
                        <Radio color="secondary" value="trial" checked={plan==="trial"} onChange={(e) => handlePlan(e)} />
                      </Stack>

                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="h4" color={"secondary.main"}>{(trialSalary/10000).toLocaleString()}만원</Typography>
                        <Typography variant="h6" color={"secondary.main"}>/ 2주</Typography>
                      </Stack>

                      <Stack direction="row" spacing={.5} alignItems="center" sx={{ my: 1 }}>
                        <SvgIconStyle src={`/icons/ic_clock.svg`} sx={{ width:20, height: 20, color: "text.disabled" }} />
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          <b>주 {candidate.trial_working_hours_per_week}시간</b> 근무
                        </Typography>
                      </Stack>

                      <Collapse
                        in={smUp || isTrialExpanded}
                        {...(isTrialExpanded ? { timeout: 500 } : {})}
                      >
                      <Typography variant="body2">
                        크루의 작업 방식, 속도를 검증하고 시작하고 싶은 기업이 선택하는 플랜이에요. 2주 동안만 크루와 일해보고, 정식 계약을 결정할 수 있어요.
                      </Typography>

                      <Divider sx={{ my: 3 }} />

                      <Stack spacing={.5}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <SvgIconStyle src={`/icons/icon-coin-mono.svg`} sx={{ color: "text.disabled" }} />
                          <Typography variant="h6">
                            계약 금액
                          </Typography>
                        </Stack>
                        <Stack sx={{ pl: 3 }} spacing={1}>
                          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ backgroundColor: "rgba(51, 102, 255, 0.08)", px: 2, py: 1, borderRadius: 1 }}>
                            <Typography variant="subtitle2">
                              총 금액
                            </Typography>
                            <Typography variant="subtitle1">
                              {trialFinTotal.toLocaleString()}원
                            </Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between" sx={{ px: 2 }}>
                            <Typography variant="body2">
                              크루 인건비
                            </Typography>
                            <Typography variant="subtitle2" sx={{ color: "secondary.main" }}>
                              {trialSalary.toLocaleString()}원
                            </Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between" sx={{ px: 2 }}>
                            <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                              수수료 ({trialFeeRate}%)
                            </Typography>
                            <Typography variant="subtitle2">
                              {trialFee.toLocaleString()}원
                            </Typography>
                          </Stack>
                          {trialFeeDcAmount > 0 && (
                          <Stack direction="row" justifyContent="space-between" sx={{ px: 2 }}>
                            <Typography variant="body2">
                              첫달 수수료할인 {!smUp && <br />}<CaptionSecondaryText>예상금액</CaptionSecondaryText>
                            </Typography>
                            <Typography variant="body2">
                              - {trialFeeDcAmount.toLocaleString()}원
                            </Typography>
                          </Stack>
                          )}
                          {trialDcAmount > 0 && (
                          <Stack direction="row" justifyContent="space-between" sx={{ px: 2 }}>
                            <Typography variant="body2">
                              쿠폰할인 {!smUp && <br />}<CaptionSecondaryText>예상금액</CaptionSecondaryText>
                            </Typography>
                            <Typography variant="body2">
                              - {trialDcAmount.toLocaleString()}원
                            </Typography>
                          </Stack>
                          )}
                          <Stack direction="row" justifyContent="space-between" sx={{ px: 2 }}>
                            <Typography variant="body2">
                              부가세
                            </Typography>
                            <Typography variant="body2">
                              {trialFinVat.toLocaleString()}원
                            </Typography>
                          </Stack>
                        </Stack>
                      </Stack>
                      </Collapse>
                    </CardActionArea>

                    {/* collapse button */}
                    {!smUp && (
                      <Button fullWidth onClick={toggleExpandTrial} size="small"
                        sx={{ mb: 2, color: "text.disabled" }}
                        endIcon={<Iconify icon={isTrialExpanded ? "eva:chevron-up-fill" : "eva:chevron-down-fill"} sx={{ width: 24, height: 24, color: "text.disabled" }} />}
                      >
                        {isTrialExpanded ? "간략히 보기" : "금액 상세 정보 보기"}
                      </Button>
                    )}
                  </Card>
                  )}

                  {/* 8주 longterm */}
                  <Card
                    sx={{
                      width: 1,
                      borderRadius: 2,
                      border: "2px solid rgba(145, 158, 171, 0.24)",
                      ...(plan === "longterm") && {
                        backgroundColor: "grey.200",
                        border: (theme) => `2px solid ${theme.palette.secondary.main}`,
                      },
                      ...candidate.status !== 'offer-to-company' && {
                        boxShadow: "none",
                      }
                    }}
                  >
                    <CardActionArea sx={{ p: 3 }} onClick={() => selectLongterm()} disabled={candidate.status !== 'offer-to-company'}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="subtitle1">
                            8주 장기 계약
                          </Typography>
                          <Typography variant="subtitle2" sx={{ color: "text.disabled" }}>
                            플랜
                          </Typography>
                        </Stack>
                        <Radio color="secondary" value="longterm" checked={plan==="longterm"} onChange={(e) => handlePlan(e)} />
                      </Stack>

                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="h4" color={"secondary.main"}>{(longtermSalary/10000).toLocaleString()}만원</Typography>
                        <Typography variant="h6" color={"secondary.main"}>/ 8주</Typography>
                      </Stack>

                      <Stack direction="row" spacing={.5} alignItems="center" sx={{ my: 1 }}>
                        <SvgIconStyle src={`/icons/ic_clock.svg`} sx={{ width:20, height: 20, color: "text.disabled" }} />
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          <b>주 {candidate.trial_working_hours_per_week}시간</b> 근무
                        </Typography>
                      </Stack>

                      <Collapse
                        in={smUp || isLongtermExpanded}
                        {...(isLongtermExpanded ? { timeout: 500 } : {})}
                      >
                      <Typography variant="body2">
                        최소 2달의 근속이 필요한 경우, 장기로 계약할 수 있어요. 필요 시 8주 이상의 기간도 협의해드립니다.
                      </Typography>

                      <Divider sx={{ my: 3 }} />

                      <Stack spacing={.5}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <SvgIconStyle src={`/icons/icon-coin-mono.svg`} sx={{ color: "text.disabled" }} />
                          <Typography variant="h6">
                            계약 금액
                          </Typography>
                        </Stack>
                        <Stack sx={{ pl: 3 }} spacing={1}>
                          <Stack direction="row" justifyContent="space-between" sx={{ backgroundColor: "rgba(51, 102, 255, 0.08)", px: 2, py: 1, borderRadius: 1 }}>
                            <Typography variant="subtitle2">
                              총 금액
                            </Typography>
                            <Typography variant="subtitle1">
                              {longtermFinTotal.toLocaleString()}원
                            </Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between" sx={{ px: 2 }}>
                            <Typography variant="body2">
                              크루 인건비
                            </Typography>
                            <Typography variant="subtitle2" sx={{ color: "secondary.main" }}>
                              {longtermSalary.toLocaleString()}원
                            </Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between" sx={{ px: 2 }}>
                            <Typography variant="subtitle2" sx={{ color: "text.secondary" }}>
                              수수료 ({longtermFeeRate}%)
                            </Typography>
                            <Typography variant="subtitle2">
                              {longtermFee.toLocaleString()}원
                            </Typography>
                          </Stack>
                          {longtermFeeDcAmount > 0 && (
                          <Stack direction="row" justifyContent="space-between" sx={{ px: 2 }}>
                            <Typography variant="body2">
                              첫달 수수료할인 {!smUp && <br />}<CaptionSecondaryText>예상금액</CaptionSecondaryText>
                            </Typography>
                            <Typography variant="body2">
                              - {longtermFeeDcAmount.toLocaleString()}원
                            </Typography>
                          </Stack>
                          )}
                          {longtermDcAmount > 0 && (
                          <Stack direction="row" justifyContent="space-between" sx={{ px: 2 }}>
                            <Typography variant="body2">
                              쿠폰할인 {!smUp && <br />}<CaptionSecondaryText>예상금액</CaptionSecondaryText>
                            </Typography>
                            <Typography variant="body2">
                              - {longtermDcAmount.toLocaleString()}원
                            </Typography>
                          </Stack>
                          )}
                          <Stack direction="row" justifyContent="space-between" sx={{ px: 2 }}>
                            <Typography variant="body2">
                              부가세
                            </Typography>
                            <Typography variant="body2">
                              {longtermFinVat.toLocaleString()}원
                            </Typography>
                          </Stack>
                        </Stack>
                      </Stack>

                      </Collapse>
                    </CardActionArea>

                    {/* collapse button */}
                    {!smUp && (
                      <Button fullWidth onClick={toggleExpandLongterm} size="small"
                        sx={{ mb: 2, color: "text.disabled" }}
                        endIcon={<Iconify icon={isLongtermExpanded ? "eva:chevron-up-fill" : "eva:chevron-down-fill"} sx={{ width: 24, height: 24, color: "text.disabled" }} />}
                      >
                        {isLongtermExpanded ? "간략히 보기" : "금액 상세 정보 보기"}
                      </Button>
                    )}
                  </Card>
                </Stack>
                ) : (                
                <Stack sx={{ width: 1 }} direction={{ xs: "column", sm: "row" }} alignItems="flex-start" spacing={2}>
                  {/* 4 + 2주 플랜 */}
                  {/* 4주 플랜 */}
                  <Card
                    sx={{
                      width: 1,
                      borderRadius: 2,
                      border: "1px solid rgba(145, 158, 171, 0.24)",
                      ...(plan === "4weeks") && {
                        backgroundColor: "grey.200",
                        border: (theme) => `2px solid ${theme.palette.secondary.main}`,
                      },
                      ...candidate.status !== 'offer-to-company' && {
                        boxShadow: "none",
                      }
                    }}
                  >
                    <CardActionArea sx={{ p: 3, pb: {xs: 2, sm: 3 } }} onClick={() => select4weeks()} disabled={candidate.status !== 'offer-to-company'}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="subtitle1">
                            4주 단위 계약
                          </Typography>
                          <Typography variant="subtitle2" sx={{ color: "text.disabled" }}>
                            플랜
                          </Typography>
                        </Stack>
                        <Radio color="secondary" value="4weeks" checked={plan==="4weeks"} onChange={(e) => handlePlan(e)} />
                      </Stack>

                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="h4" color={"secondary.main"}>{(salary/10000).toLocaleString()}만원</Typography>
                        <Typography variant="h6" color={"secondary.main"}>/ 4주</Typography>
                      </Stack>

                      <Stack direction="row" spacing={.5} alignItems="center" sx={{ mb: 1 }}>
                        <SvgIconStyle src={`/icons/ic_clock.svg`} sx={{ width:20, height: 20, color: "text.disabled" }} />
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          <b>주 {candidate.working_hours_per_week}시간</b> 근무
                        </Typography>
                      </Stack>

                      <Collapse
                        in={smUp || is4weeksExpanded}
                        {...(is4weeksExpanded ? { timeout: 500 } : {})}
                      >
                      <Typography variant="body2">
                        크루가 제안한 4주 기준 인건비와 주당 업무 시간이에요.<br/>
                        주당 업무 시간과 인건비는 미팅 때 협의하여 변경할 수 있어요.
                      </Typography>

                      <Divider sx={{ my: 3 }} />

                      <Stack spacing={.5} sx={{ mb: 3 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <SvgIconStyle src={`/icons/icon-coin-mono.svg`} sx={{ color: "text.disabled" }} />
                          <Typography variant="h6">
                            계약 금액
                          </Typography>
                        </Stack>
                        <Stack sx={{ pl: 4 }} spacing={1}>
                          <Stack direction="row" justifyContent="space-between" sx={{ backgroundColor: "rgba(51, 102, 255, 0.08)", px: 2, py: 1, borderRadius: 1 }}>
                            <Typography variant="subtitle2">
                              총 금액 {smUp && (<CaptionSecondaryText>4주(28일) 기준</CaptionSecondaryText>)}
                            </Typography>
                            <Typography variant="subtitle2">
                              {finTotal.toLocaleString()}원
                            </Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between" sx={{ px: 2 }}>
                            <Typography variant="body2">
                              크루 인건비
                            </Typography>
                            <Typography variant="subtitle2" sx={{ color: "secondary.main" }}>
                              {salary.toLocaleString()}원
                            </Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between" sx={{ px: 2 }}>
                            <Typography variant="body2">
                              수수료
                            </Typography>
                            <Typography variant="body2">
                              {fee.toLocaleString()}원
                            </Typography>
                          </Stack>
                          {feeDcAmount > 0 && (
                          <Stack direction="row" justifyContent="space-between" sx={{ px: 2 }}>
                            <Typography variant="body2">
                              첫달 수수료할인 <CaptionSecondaryText>예상금액</CaptionSecondaryText>
                            </Typography>
                            <Typography variant="body2">
                              - {feeDcAmount.toLocaleString()}원
                            </Typography>
                          </Stack>
                          )}
                          {dcAmount > 0 && (
                          <Stack direction="row" justifyContent="space-between" sx={{ px: 2 }}>
                            <Typography variant="body2">
                              쿠폰할인 <CaptionSecondaryText>예상금액</CaptionSecondaryText>
                            </Typography>
                            <Typography variant="body2">
                              - {dcAmount.toLocaleString()}원
                            </Typography>
                          </Stack>
                          )}
                          <Stack direction="row" justifyContent="space-between" sx={{ px: 2 }}>
                            <Typography variant="body2">
                              부가세
                            </Typography>
                            <Typography variant="body2">
                              {finVat.toLocaleString()}원
                            </Typography>
                          </Stack>
                        </Stack>
                      </Stack>

                      <Stack spacing={.5}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <SvgIconStyle src={`/icons/ic_clock.svg`} sx={{ color: "text.disabled" }} />
                          <Typography variant="h6">
                            주당 업무 가능한 시간
                          </Typography>
                        </Stack>
                        <Stack direction="row" sx={{ pl: 4 }} spacing={1}>
                          <Typography sx={{ color: "secondary.main" }} variant="subtitle1">
                            주 {candidate.working_hours_per_week}시간
                          </Typography>
                          <Typography sx={{ color: "text.secondary" }} variant="body2">
                            (4주 총 {4*candidate.working_hours_per_week}시간)
                          </Typography>
                        </Stack>
                      </Stack>
                      </Collapse>
                    </CardActionArea>

                    {/* collapse button */}
                    {!smUp && (
                      <Button fullWidth onClick={toggleExpand4weeks} size="small"
                        sx={{ mb: 2, color: "text.disabled" }}
                        endIcon={<Iconify icon={is4weeksExpanded ? "eva:chevron-up-fill" : "eva:chevron-down-fill"} sx={{ width: 24, height: 24, color: "text.disabled" }} />}
                      >
                        {is4weeksExpanded ? "간략히 보기" : "금액 상세 정보 보기"}
                      </Button>
                    )}
                  </Card>

                  {/* 2주 trial */}
                  <Card
                    sx={{
                      width: 1,
                      borderRadius: 2,
                      border: "1px solid rgba(145, 158, 171, 0.24)",
                      ...(plan === "trial") && {
                        backgroundColor: "grey.200",
                        border: (theme) => `2px solid ${theme.palette.secondary.main}`,
                      },
                      ...candidate.status !== 'offer-to-company' && {
                        boxShadow: "none",
                      }
                    }}
                  >
                    <CardActionArea sx={{ p: 3 }} onClick={() => selectTrial()} disabled={candidate.status !== 'offer-to-company'}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <Typography variant="subtitle1">
                            2주 50만원 체험판
                          </Typography>
                          <Typography variant="subtitle2" sx={{ color: "text.disabled" }}>
                            플랜
                          </Typography>
                        </Stack>
                        <Radio color="secondary" value="trial" checked={plan==="trial"} onChange={(e) => handlePlan(e)} />
                      </Stack>

                      <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="h4" color={"secondary.main"}>{(trialSalary/10000).toLocaleString()}만원</Typography>
                        <Typography variant="h6" color={"secondary.main"}>/ 2주</Typography>
                      </Stack>

                      <Stack direction="row" spacing={.5} alignItems="center" sx={{ mb: 1 }}>
                        <SvgIconStyle src={`/icons/ic_clock.svg`} sx={{ width:20, height: 20, color: "text.disabled" }} />
                        <Typography variant="body2" sx={{ color: "text.secondary" }}>
                          <b>주 {candidate.trial_working_hours_per_week}시간</b> 근무
                        </Typography>
                      </Stack>

                      <Collapse
                        in={smUp || isTrialExpanded}
                        {...(isTrialExpanded ? { timeout: 500 } : {})}
                      >
                      <Typography variant="body2">
                        2주 동안 50만원 만큼만 크루와 일해보고, 이 크루와 더 일할지 말지 결정할 수 있어요.<br />
                        크루가 제안한 4주 기준 금액과 시간을 시급 계산했어요.
                      </Typography>

                      <Divider sx={{ my: 3 }} />

                      <Stack spacing={.5} sx={{ mb: 3 }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <SvgIconStyle src={`/icons/icon-coin-mono.svg`} sx={{ color: "text.disabled" }} />
                          <Typography variant="h6">
                            계약 금액
                          </Typography>
                        </Stack>
                        <Stack sx={{ pl: 4 }} spacing={1}>
                          <Stack direction="row" justifyContent="space-between" sx={{ backgroundColor: "rgba(51, 102, 255, 0.08)", px: 2, py: 1, borderRadius: 1 }}>
                            <Typography variant="subtitle2">
                              총 금액 {smUp && (<CaptionSecondaryText>2주(14일) 기준</CaptionSecondaryText>)}
                            </Typography>
                            <Typography variant="subtitle2">
                              {trialFinTotal.toLocaleString()}원
                            </Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between" sx={{ px: 2 }}>
                            <Typography variant="body2">
                              크루 인건비
                            </Typography>
                            <Typography variant="subtitle2" sx={{ color: "secondary.main" }}>
                              {trialSalary.toLocaleString()}원
                            </Typography>
                          </Stack>
                          <Stack direction="row" justifyContent="space-between" sx={{ px: 2 }}>
                            <Typography variant="body2">
                              수수료
                            </Typography>
                            <Typography variant="body2">
                              {trialFee.toLocaleString()}원
                            </Typography>
                          </Stack>
                          {trialFeeDcAmount > 0 && (
                          <Stack direction="row" justifyContent="space-between" sx={{ px: 2 }}>
                            <Typography variant="body2">
                              첫달 수수료할인 <CaptionSecondaryText>예상금액</CaptionSecondaryText>
                            </Typography>
                            <Typography variant="body2">
                              - {trialFeeDcAmount.toLocaleString()}원
                            </Typography>
                          </Stack>
                          )}
                          {trialDcAmount > 0 && (
                          <Stack direction="row" justifyContent="space-between" sx={{ px: 2 }}>
                            <Typography variant="body2">
                              쿠폰할인 <CaptionSecondaryText>예상금액</CaptionSecondaryText>
                            </Typography>
                            <Typography variant="body2">
                              - {trialDcAmount.toLocaleString()}원
                            </Typography>
                          </Stack>
                          )}
                          <Stack direction="row" justifyContent="space-between" sx={{ px: 2 }}>
                            <Typography variant="body2">
                              부가세
                            </Typography>
                            <Typography variant="body2">
                              {trialFinVat.toLocaleString()}원
                            </Typography>
                          </Stack>
                        </Stack>
                      </Stack>

                      <Stack spacing={.5}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <SvgIconStyle src={`/icons/ic_clock.svg`} sx={{ color: "text.disabled" }} />
                          <Typography variant="h6">
                            주당 업무 가능한 시간
                          </Typography>
                        </Stack>
                        <Stack direction="row" sx={{ pl: 4 }} spacing={1}>
                          <Typography sx={{ color: "secondary.main" }} variant="subtitle1">
                            주 {candidate.trial_working_hours_per_week}시간
                          </Typography>
                          <Typography sx={{ color: "text.secondary" }} variant="body2">
                            (2주 총 {2*candidate.trial_working_hours_per_week}시간)
                          </Typography>
                        </Stack>
                      </Stack>
                      </Collapse>
                    </CardActionArea>

                    {/* collapse button */}
                    {!smUp && (
                      <Button fullWidth onClick={toggleExpandTrial} size="small"
                        sx={{ mb: 2, color: "text.disabled" }}
                        endIcon={<Iconify icon={isTrialExpanded ? "eva:chevron-up-fill" : "eva:chevron-down-fill"} sx={{ width: 24, height: 24, color: "text.disabled" }} />}
                      >
                        {isTrialExpanded ? "간략히 보기" : "금액 상세 정보 보기"}
                      </Button>
                    )}
                  </Card>
                </Stack>
                )}
              </Stack>

              {candidate.status == 'waiting-3way-meeting' && (
                <FormStyle>
                  <Typography variant="body2">크루에게 미팅 시간을 전달하였습니다. 크루가 시간을 확인하면, 미팅이 확정됩니다.</Typography>
                </FormStyle>
              )}
              {candidate.status == 'offer-to-company' && (
                <>
                <FormStyle>
                  <Typography>
                    크루와 진행하는 Zoom 미팅은 미팅 위반 행위(크루의 개인정보를 묻는 행위, 욕설 등) 체크를 위해 <DIOText/>에서 레코딩하며, 7일간 저장 후 파기됩니다.
                  </Typography>
                  <Typography>
                    동의하시는 경우에만 사전 미팅을 예약해 주세요.
                  </Typography>
                </FormStyle>
                <Stack spacing={2} alignItems="flex-end" sx={{ mt: 2 }}>
                  {smUp && (
                  <Stack direction="row">
                    {!jobPost.coffee_chat && (
                    <LoadingButton size="large" id={EVENT_ID.bizRejectOffer} type="button" loading={isSubmit} onClick={openBizRejectDialog} sx={{ mr: 3 }}>
                      다른 크루를 매칭 받고 싶어요
                    </LoadingButton>
                    )}
                    <LoadingButton size="large" id={EVENT_ID.bizAcceptOffer} type="button" variant="contained" loading={isSubmit} onClick={reserve3WayMeetingOrSaveCard} sx={{ px: 3 }}>
                      이 크루와 미팅 예약하기
                    </LoadingButton>
                  </Stack>
                  )}
                  {!smUp && (
                  <>
                    <LoadingButton id={EVENT_ID.bizAcceptOffer} fullWidth type="button" variant="contained" loading={isSubmit} onClick={reserve3WayMeetingOrSaveCard}>
                      이 크루와 미팅 예약하기
                    </LoadingButton>
                    <LoadingButton id={EVENT_ID.bizRejectOffer} fullWidth type="button" loading={isSubmit} onClick={openBizRejectDialog}>
                      다른 크루를 매칭 받고 싶어요
                    </LoadingButton>
                  </>
                  )}
                </Stack>
                <RejectOrModifyCrewDialog
                  isOpen={bizRejectDialog}
                  closeDialog={closeBizRejectDialog}
                  jobPost={jobPost}
                  candidate={candidate}
                  passOffer={passOffer}
                  stopJobPost={stopJobPost}
                  modifyOffer={modifyOffer}
                />
                </>
              )}
            </CardContent>
          </Card>
          </Collapse>
        </AccordionDetails>
      </Accordion>
      )}

      <EasySalaryDialog isOpen={easySalaryOpen} close={() => setEasySalaryOpen(false)} />
      <IsRightSalaryDialog
        isOpen={isRightSalaryOpen}
        close={() => setIsRightSalaryOpen(false)}
        crewNickname={candidate?.crew_nickname}
        crewPosition={candidate?.crew_position}
        average={candidate?.average_salary}
        detailIndex={candidate?.crew_year_range}
      />
      <NegoExtendDialog
        deadline={candidate?.nego_extendable_date}
        isOpen={openExtendDialog}
        close={() => setOpenExtendDialog(false)}
        extend={extendNego}
        isSubmit={isSubmit}
      />
      <InvitationNudgeSnackbar
        open={isOpenNudge}
        close={closeNudge}
        nudgeID={EVENT_ID.bizInvitationNudgeOffer}
      />
      <SelectPlanWarnDialog
        isOpen={isOpenPlanWarnDialog}
        closeDialog={() => setIsOpenPlanWarnDialog(false)}
      />
      <MeetingDepositPaymentDialog
        isOpen={isOpenDepositPaymentDialog}
        closeDialog={() => setIsOpenDepositPaymentDialog(false)}
        jobPostId={jobPost?.id}
        candidateId={candidate?.id}
        paid={reserve3WayMeeting}
      />

    </>
  );
};
